import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { StatoSessioneService } from 'app/shared/services/stato-sessione.service';
import { Observable, BehaviorSubject, combineLatest, of, Subject, from } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, startWith } from 'rxjs/operators';
import { TestIdService, TestWithMetadata } from '../test-id.service';
import {
  DynamicRefinedTestCase,
  GroupingRequest,
  RawTestCase,
  RefinementRequest
} from '../../../../shared/templateGen/templateInterfaces';
import { DownloaderComponent } from '../downloader/downloader.component';
import { ToastrService } from 'ngx-toastr';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import Swal from 'sweetalert2';
import { UserStoryService } from '../../../../shared/templateGen/template-api.service';
import { SessionPersistenceService } from '../../session-manager/session-persistence.service';
import { MacroScenarioItem } from '../../template/three-column-workspace/three-column-workspace.component';

// Define the shape of your new, clean data structure
interface GroupedViewData {
  macro: MacroScenarioItem;
  tests: TestWithMetadata[];
}

interface GroupedConsolidatedViewData {
  macro: MacroScenarioItem;
  consolidatedTests: TestWithMetadata[];
  originalRawTestsCount: number; // How many raw tests were in this macro before consolidation
}

@Component({
  selector: 'app-testbook',
  templateUrl: './testbook.component.html',
  styleUrls: ['./testbook.component.scss']
})
export class TestbookComponent implements OnInit, OnDestroy {
  // --- UI Observables ---
  public groupedGeneratedTestsData$: Observable<GroupedViewData[]>;
  public groupedGeneratedTestsWithSequentialIds$: Observable<GroupedViewData[]>;
  public groupedConsolidatedTestsWithSequentialIds$: Observable<GroupedConsolidatedViewData[]>;
  public allConsolidatedTestsWithSequentialIds$: Observable<TestWithMetadata[]>;
  macroScenarios$: Observable<MacroScenarioItem[]>;
  generatedTests$: Observable<TestWithMetadata[]>;
  displayableColumns$: Observable<string[]>;
  exportButtonsVisible$: Observable<boolean>;
  globalConsolidatedTests$: Observable<TestWithMetadata[]>;
  public groupedConsolidatedTestsData$: Observable<GroupedConsolidatedViewData[]>;

  // --- View State ---
  private displaySourceSubject = new BehaviorSubject<'global-consolidated' | 'generated'>('generated');
  displaySource$ = this.displaySourceSubject.asObservable();
  showAllGeneratedView = true;
  showAllConsolidatedView = true;

  // --- Consolidation State ---
  isGrouping = false;
  isConsolidating = false;
  consolidationProgress = { current: 0, total: 0 };

  // --- Properties for Filtering ---
  public filteredConsolidatedTests$: Observable<TestWithMetadata[]>;
  public filteredRawTests$: Observable<TestWithMetadata[]>;

  // Consolidated Test Filters
  public currentTestTypeFilter: string | null = null;
  // --- MODIFIED: This is now an observable to be reactive ---
  public testTypesWithCounts$: Observable<{ name: string; count: number }[]>;
  private testTypeFilterSubject = new BehaviorSubject<string | null>(null);

  // Review Filter (Consolidated Only)
  public currentReviewFilter: 'all' | 'review' | 'non-review' = 'all';
  public testsToReviewCount$: Observable<number>;
  public testsWithoutReviewCount$: Observable<number>;
  private reviewFilterSubject = new BehaviorSubject<'all' | 'review' | 'non-review'>('all');

  // Raw Test Filters
  public currentRawTestTypeFilter: string | null = null;
  public rawTestTypesWithCounts: { name: string; count: number }[] = [];
  private rawTestTypeFilterSubject = new BehaviorSubject<string | null>(null);

  // KPI Observables
  public totalTestsCount$: Observable<number>;
  public totalRawTestsCount$: Observable<number>;

  private microScenarioRelatedKeys: string[] = [
    'microScenarioId', 'originalMicroScenarioId', 'microId', 'sourceMicroScenarioId',
    'microScenarioRef', 'microScenarioTitle', 'microScenarioText',
  ];

  private destroy$ = new Subject<void>();
  private excelExportTimeoutId: any = null;

  constructor(
    private statoSessioneService: StatoSessioneService,
    private downloader: DownloaderComponent,
    private toastr: ToastrService,
    private spinnerService: SpinnerService,
    private testIdService: TestIdService,
    private userStoryService: UserStoryService,
    private cd: ChangeDetectorRef,
    private sessionPersistService: SessionPersistenceService,
  ) { }

  ngOnInit(): void {
    // --- SOURCE OBSERVABLES ---

    this.generatedTests$ = this.statoSessioneService.generatedTests$.pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.macroScenarios$ = this.statoSessioneService.macroScenarios$.pipe(
      startWith([]),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.globalConsolidatedTests$ = this.statoSessioneService.globalConsolidatedTests$.pipe(
      map(dynamicTests => this.transformToDisplayable(dynamicTests || [])),
      map(displayableTests => this.testIdService.sortAndSequenceTestsByType(displayableTests)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.allConsolidatedTestsWithSequentialIds$ = this.globalConsolidatedTests$.pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // --- NEW: Set default view based on consolidated tests presence ---
    this.allConsolidatedTestsWithSequentialIds$.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(consolidatedTests => {
      if (consolidatedTests && consolidatedTests.length > 0) {
        this.displaySourceSubject.next('global-consolidated');
      }
    });

    // --- KPI & COUNT OBSERVABLES ---
    this.totalRawTestsCount$ = this.generatedTests$.pipe(map(tests => tests?.length || 0));
    this.totalTestsCount$ = this.allConsolidatedTestsWithSequentialIds$.pipe(map(tests => tests?.length || 0));


    // --- MODIFICATION FOR INTERDEPENDENT FILTERS ---
    // 1. Create intermediate streams that are filtered by only ONE of the criteria.
    const testsFilteredByType$ = combineLatest([
      this.allConsolidatedTestsWithSequentialIds$,
      this.testTypeFilterSubject.asObservable()
    ]).pipe(
      map(([tests, activeTypeFilter]) => {
        if (!activeTypeFilter) return tests;
        return tests.filter(test => this.getTestType(test) === activeTypeFilter);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const testsFilteredByReview$ = combineLatest([
      this.allConsolidatedTestsWithSequentialIds$,
      this.reviewFilterSubject.asObservable()
    ]).pipe(
      map(([tests, activeReviewFilter]) => {
        if (activeReviewFilter === 'all') return tests;
        if (activeReviewFilter === 'review') {
          return tests.filter(test => this.isTestForReview(test));
        }
        // 'non-review'
        return tests.filter(test => !this.isTestForReview(test));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // 2. Base the count observables on these new intermediate streams.
    // Review counts now respect the currently selected TYPE filter.
    this.testsToReviewCount$ = testsFilteredByType$.pipe(
      map(tests => tests?.filter(t => this.isTestForReview(t)).length || 0)
    );
    this.testsWithoutReviewCount$ = testsFilteredByType$.pipe(
      map(tests => tests?.filter(t => !this.isTestForReview(t)).length || 0)
    );

    // Type counts now respect the currently selected REVIEW filter.
    this.testTypesWithCounts$ = testsFilteredByReview$.pipe(
      map(tests => {
        const typeMap = new Map<string, number>();
        const orderedTypes: string[] = [];
        (tests || []).forEach(test => {
          const testType = this.getTestType(test);
          typeMap.set(testType, (typeMap.get(testType) || 0) + 1);
          if (!orderedTypes.includes(testType)) {
            orderedTypes.push(testType);
          }
        });
        return Array.from(typeMap.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => orderedTypes.indexOf(a.name) - orderedTypes.indexOf(b.name));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    // --- END OF INTERDEPENDENT FILTER MODIFICATION ---


    // Calculate counts for RAW test filter buttons (this logic remains independent)
    this.generatedTests$.pipe(takeUntil(this.destroy$)).subscribe(tests => {
      const typeMap = new Map<string, number>();
      const orderedTypes: string[] = [];
      (tests || []).forEach(test => {
        const testType = this.getTestType(test);
        typeMap.set(testType, (typeMap.get(testType) || 0) + 1);
        if (!orderedTypes.includes(testType)) {
          orderedTypes.push(testType);
        }
      });
      this.rawTestTypesWithCounts = Array.from(typeMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => orderedTypes.indexOf(a.name) - orderedTypes.indexOf(b.name));
    });

    // --- FILTERED TEST OBSERVABLES (Final display list) ---
    // This observable remains the same, as it correctly combines all filters for the final display.
    this.filteredConsolidatedTests$ = combineLatest([
      this.allConsolidatedTestsWithSequentialIds$,
      this.testTypeFilterSubject.asObservable(),
      this.reviewFilterSubject.asObservable()
    ]).pipe(
      map(([tests, activeTypeFilter, activeReviewFilter]) => {
        let filteredTests = tests;
        if (activeTypeFilter) {
          filteredTests = filteredTests.filter(test => this.getTestType(test) === activeTypeFilter);
        }
        if (activeReviewFilter === 'review') {
          filteredTests = filteredTests.filter(test => this.isTestForReview(test));
        } else if (activeReviewFilter === 'non-review') {
          filteredTests = filteredTests.filter(test => !this.isTestForReview(test));
        }
        return filteredTests;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.filteredRawTests$ = combineLatest([
      this.generatedTests$,
      this.rawTestTypeFilterSubject.asObservable()
    ]).pipe(
      map(([tests, activeFilter]) => {
        if (!activeFilter) { return tests; }
        return tests.filter(test => this.getTestType(test) === activeFilter);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // --- OTHER SETUP (UNCHANGED) ---
    const globalConsolidatedCols$ = this.globalConsolidatedTests$.pipe(map(displayableTests => { if (displayableTests && displayableTests.length > 0 && displayableTests[0]?.data) { const allKeys = Object.keys(displayableTests[0].data); return [...new Set(['ID', ...allKeys.filter(key => !this.microScenarioRelatedKeys.includes(key))])]; } return ['ID']; }), startWith(['ID']));
    const generatedCols$ = this.statoSessioneService.columns$.pipe(map(cols => ['ID', ...new Set(cols.filter(c => c.toLowerCase() !== 'id' && c !== 'metadata'))]), startWith(['ID']));
    this.displayableColumns$ = this.displaySourceSubject.pipe(switchMap(source => source === 'global-consolidated' ? globalConsolidatedCols$ : generatedCols$), shareReplay({ bufferSize: 1, refCount: true }));
    this.groupedGeneratedTestsData$ = combineLatest([this.macroScenarios$, this.generatedTests$]).pipe(map(([macros, genTests]) => { if (!macros?.length || !genTests?.length) return []; return macros.map(macro => ({ macro: macro, tests: genTests.filter(test => test.metadata.macroScenarioId === macro.id) })); }), shareReplay({ bufferSize: 1, refCount: true }));
    this.groupedGeneratedTestsWithSequentialIds$ = this.groupedGeneratedTestsData$;
    const consolidatedHasData$ = this.globalConsolidatedTests$.pipe(map(tests => !!tests?.length));
    const generatedHasData$ = this.generatedTests$.pipe(map(tests => !!tests?.length));
    this.exportButtonsVisible$ = this.displaySourceSubject.pipe(switchMap(source => source === 'global-consolidated' ? consolidatedHasData$ : generatedHasData$), startWith(false));
    this.groupedConsolidatedTestsData$ = combineLatest([this.macroScenarios$, this.globalConsolidatedTests$, this.generatedTests$, this.statoSessioneService.microScenarioSpecifications$]).pipe(map(([macros, consolidatedTests, rawTests, microSpecsMap]) => { if (!macros?.length || !consolidatedTests?.length) return []; const microToMacroMap = new Map<string, string>(); if (microSpecsMap) { microSpecsMap.forEach((macroState, macroId) => { if (macroState.specifications) { macroState.specifications.forEach(microSpec => microToMacroMap.set(microSpec.id, macroId)); } }); } rawTests?.forEach(test => { if (test.metadata.microScenarioId && test.metadata.macroScenarioId) { microToMacroMap.set(test.metadata.microScenarioId, test.metadata.macroScenarioId); } }); return macros.map(macro => { const testsForThisMacro = consolidatedTests.filter(consolidatedTest => { const originalTest = this.findOriginalConsolidatedTest(consolidatedTest.metadata.displayId); return originalTest?.sourceMicroScenarioIds?.some(microId => microToMacroMap.get(microId) === macro.id) || false; }); const originalRawTestsCount = rawTests?.filter(test => test.metadata.macroScenarioId === macro.id).length || 0; return { macro: macro, consolidatedTests: testsForThisMacro, originalRawTestsCount: originalRawTestsCount }; }).filter(group => group.consolidatedTests.length > 0 || group.originalRawTestsCount > 0); }), shareReplay({ bufferSize: 1, refCount: true }));
    this.groupedConsolidatedTestsWithSequentialIds$ = this.groupedConsolidatedTestsData$;
  }

  /**
   * Find the original DynamicRefinedTestCase by its display ID
   * This is needed to access the sourceMicroScenarioIds which are lost in the transformation
   */
  private findOriginalConsolidatedTest(displayId: string): DynamicRefinedTestCase | null {
    let originalTest: DynamicRefinedTestCase | null = null;
    this.statoSessioneService.globalConsolidatedTests$.pipe(take(1)).subscribe(tests => {
      originalTest = tests?.find(test => test.id === displayId) || null;
    });
    return originalTest;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.excelExportTimeoutId) {
      clearTimeout(this.excelExportTimeoutId);
    }
  }

  toggleGeneratedView(): void {
    this.showAllGeneratedView = !this.showAllGeneratedView;
  }

  toggleConsolidatedView(): void {
    this.showAllConsolidatedView = !this.showAllConsolidatedView;
  }

  calculateReductionPercentage(originalCount: number, finalCount: number): number {
    if (originalCount === 0) return 0; // Avoid division by zero
    return Math.round((1 - finalCount / originalCount) * 100);
  }

  public transformToDisplayable(refinedTests: DynamicRefinedTestCase[]): TestWithMetadata[] {
    if (!refinedTests) return [];
    return refinedTests.map((refinedTest, index) => ({
      metadata: {
        displayId: refinedTest.id, testId: refinedTest.id, sequentialId: index + 1,
        testIndex: index, gherkinSection: 'when', source: 'GlobalConsolidated'
      },
      data: refinedTest.data || {}
    }));
  }

  private async getTestsToExport(): Promise<TestWithMetadata[]> {
    const currentSource = this.displaySourceSubject.getValue();
    if (currentSource === 'global-consolidated') {
      return await this.filteredConsolidatedTests$.pipe(take(1)).toPromise() || [];
    } else {
      return await this.filteredRawTests$.pipe(take(1)).toPromise() || [];
    }
  }

  async downloadConsolidatedCSV(): Promise<void> {
    const testsToExport = await this.getTestsToExport();
    if (!testsToExport || testsToExport.length === 0) {
      this.toastr.info('No tests available to export for the current view.');
      return;
    }
    let availableColumns = await this.displayableColumns$.pipe(take(1)).toPromise() || ['ID'];
    const columnsHtml = availableColumns.map((column, index) => `
      <div class="form-check my-2">
        <input type="checkbox" class="form-check-input swal-column-checkbox"
               id="column-tb-${index}" value="${column}" checked>
        <label class="form-check-label" for="column-tb-${index}">${column}</label>
      </div>`).join('');
    const delimiterHtml = `
      <div class="form-group mt-4">
        <label for="delimiter-tb">Delimiter:</label>
        <select id="delimiter-tb" class="form-control">
          <option value="|">Pipe (|)</option>
          <option value=",">Comma (,)</option>
          <option value=";">Semicolon (;)</option>
          <option value="\t">Tab</option>
        </select>
      </div>`;
    const selectAllHtml = `
      <div class="form-check mb-3">
        <input type="checkbox" class="form-check-input" id="select-all-tb" checked>
        <label class="form-check-label" for="select-all-tb">Select/Deselect All</label>
      </div><hr>`;
    Swal.fire({
      title: 'Select Columns for CSV Export',
      html: `<div class="text-left">${selectAllHtml}<div style="max-height: 300px; overflow-y: auto;">${columnsHtml}</div>${delimiterHtml}</div>`,
      showCancelButton: true,
      confirmButtonText: 'Export CSV',
      didOpen: () => {
        const selectAllCheckbox = document.getElementById('select-all-tb') as HTMLInputElement;
        const columnCheckboxes = document.querySelectorAll('.swal-column-checkbox') as NodeListOf<HTMLInputElement>;
        selectAllCheckbox.addEventListener('change', () => columnCheckboxes.forEach(cb => cb.checked = selectAllCheckbox.checked));
      }
    }).then(result => {
      if (result.isConfirmed) {
        const selectedSwalColumns = Array.from(document.querySelectorAll('.swal-column-checkbox:checked'))
          .map((cb: HTMLInputElement) => cb.value);
        const delimiter = (document.getElementById('delimiter-tb') as HTMLSelectElement).value;
        if (selectedSwalColumns.length === 0) {
          this.toastr.warning('Please select at least one column.');
          return;
        }
        this.downloader.exportToCSVFromData(testsToExport, `${this.displaySourceSubject.getValue()}-tests`, selectedSwalColumns, delimiter);
        this.toastr.success('CSV export initiated.');
      }
    });
  }

  async downloadConsolidatedExcel(): Promise<void> {
    const testsToExport = await this.getTestsToExport();
    if (!testsToExport || testsToExport.length === 0) {
      this.toastr.info('No tests available to export for the current view.');
      return;
    }
    let availableColumns = await this.displayableColumns$.pipe(take(1)).toPromise() || ['ID'];
    const columnsHtml = availableColumns.map((column, index) => `
      <div class="form-check my-2">
        <input type="checkbox" class="form-check-input swal-column-checkbox"
               id="column-excel-tb-${index}" value="${column}" checked>
        <label class="form-check-label" for="column-excel-tb-${index}">${column}</label>
      </div>`).join('');
    const selectAllHtml = `
      <div class="form-check mb-3">
        <input type="checkbox" class="form-check-input" id="select-all-excel-tb" checked>
        <label class="form-check-label" for="select-all-excel-tb">Select/Deselect All</label>
      </div><hr>`;
    const excelOptionsHtml = `
      <div class="form-group mt-4">
        <label for="sheetName-tb">Sheet Name:</label>
        <input type="text" class="form-control" id="sheetName-tb" value="${this.displaySourceSubject.getValue() === 'global-consolidated' ? 'FinalConsolidatedTests' : 'RawGeneratedTests'}">
      </div>
      <div class="form-check mt-3"><input type="checkbox" class="form-check-input" id="addFilters-tb" checked><label for="addFilters-tb">Add filters</label></div>
      <div class="form-check mt-2"><input type="checkbox" class="form-check-input" id="freezeHeader-tb" checked><label for="freezeHeader-tb">Freeze header</label></div>`;
    Swal.fire({
      title: 'Select Columns for Excel Export',
      html: `<div class="text-left">${selectAllHtml}<div style="max-height: 300px; overflow-y: auto;">${columnsHtml}</div>${excelOptionsHtml}</div>`,
      showCancelButton: true,
      confirmButtonText: 'Export Excel',
      width: '600px',
      didOpen: () => {
        const selectAllCheckbox = document.getElementById('select-all-excel-tb') as HTMLInputElement;
        const columnCheckboxes = document.querySelectorAll('.swal-column-checkbox') as NodeListOf<HTMLInputElement>;
        selectAllCheckbox.addEventListener('change', () => columnCheckboxes.forEach(cb => cb.checked = selectAllCheckbox.checked));
      }
    }).then(result => {
      if (result.isConfirmed) {
        const selectedSwalColumns = Array.from(document.querySelectorAll('.swal-column-checkbox:checked'))
          .map((cb: HTMLInputElement) => cb.value);
        const sheetName = (document.getElementById('sheetName-tb') as HTMLInputElement).value || `${this.displaySourceSubject.getValue()}Tests`;
        const addFilters = (document.getElementById('addFilters-tb') as HTMLInputElement).checked;
        const freezeHeader = (document.getElementById('freezeHeader-tb') as HTMLInputElement).checked;
        if (selectedSwalColumns.length === 0) {
          this.toastr.warning('Please select at least one column.');
          return;
        }
        this.spinnerService.show();
        this.downloader.exportToExcelFromData(
          testsToExport,
          `${this.displaySourceSubject.getValue()}-tests`,
          selectedSwalColumns,
          { sheetName, addFilters, freezeHeader }
        );
        if (this.excelExportTimeoutId) {
          clearTimeout(this.excelExportTimeoutId);
        }
        this.excelExportTimeoutId = setTimeout(() => {
          this.spinnerService.hide();
          this.toastr.success('Excel export completed successfully!');
          this.excelExportTimeoutId = null;
        }, 500);
      }
    });
  }

  async startGlobalConsolidation(): Promise<void> {
    const rawTests = await this.generatedTests$.pipe(take(1)).toPromise();
    if (!rawTests || rawTests.length === 0) {
      this.toastr.warning('No raw tests available to consolidate.');
      return;
    }
    const swalResult = await Swal.fire({
      title: 'Start Global Consolidation?',
      text: `This will analyze all ${rawTests.length} raw tests to find redundancies and create a new, final test suite. This may take some time.`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Yes, start!',
      cancelButtonText: 'Cancel'
    });
    if (!swalResult.isConfirmed) return;

    this.isGrouping = true;
    this.isConsolidating = false;
    this.consolidationProgress = { current: 0, total: 0 };
    this.statoSessioneService.clearGlobalConsolidationState();
    this.cd.detectChanges();

    try {
      // Step 1: Get all necessary context from the session state
      const [language, userStory, allTemplates, selectedTemplateName
      ] = await combineLatest([
        this.statoSessioneService.projectLanguage$,
        this.statoSessioneService.refinedStoryString$,
        this.statoSessioneService.templates$,
        this.statoSessioneService.selectedTemplate$
      ]).pipe(take(1)).toPromise();

      const selectedTemplateObject = allTemplates.find(template => template.templateName === selectedTemplateName);
      if (!selectedTemplateObject) {
        this.toastr.error('Could not find the selected output template.', 'Template Error');
        this._resetConsolidationState();
        return;
      }
      const selectedTemplateDesc = selectedTemplateObject.templateDescription || '';
      const selectedTemplateColumns = selectedTemplateObject.columns || [];

      // Step 2: Call the grouping service
      this.toastr.info('Phase 1: Finding test groups...');

      const groupingResponse = await this.userStoryService.groupTestsForConsolidation(
        this.statoSessioneService.getProjectId(),
        this.statoSessioneService.getSessionName(),
        userStory,
        rawTests,
        language
      ).pipe(take(1)).toPromise();

      this.statoSessioneService.updateConsolidationGroups(groupingResponse.consolidation_groups);
      const groups = groupingResponse.consolidation_groups;
      const noGroupsFound = !groups || groups.length === 0;

      // Step 3: Iterate through groups and call the refinement service
      if (!noGroupsFound) {
        this.isGrouping = false;
        this.isConsolidating = true;
        this.consolidationProgress = { current: 0, total: groups.length };
        this.toastr.info(`Phase 2: Consolidating ${groups.length} groups...`);
        this.cd.detectChanges();
        const allRawTestsMap = new Map(rawTests.map(t => [t.metadata.displayId, t]));

        for (const [index, group] of groups.entries()) {
          this.consolidationProgress.current++;
          this.cd.detectChanges();
          const testsForThisGroup = group.test_ids.map(id => allRawTestsMap.get(id)).filter((t): t is TestWithMetadata => !!t);
          if (testsForThisGroup.length === 0) continue;

          try {
            const response = await this.userStoryService.refineTestSuite(
              this.statoSessioneService.getProjectId(),
              this.statoSessioneService.getSessionName(),
              `${userStory}\n\nGroup Rationale: ${group.group_rationale}`, // user_story
              this.transformToRaw(testsForThisGroup),                     // raw_test_cases
              selectedTemplateDesc,                                      // template_description
              selectedTemplateColumns,                                   // output_template
              language                                                   // language
            ).pipe(take(1)).toPromise();

            if (response && response.tests && response.tests.length > 0) {
              const consolidatedTests = this.parseJsonToConsolidatedTests(response.tests, group.group_rationale, index, testsForThisGroup);
              this.statoSessioneService.addGlobalConsolidatedTests(consolidatedTests);
              this.statoSessioneService.addTokenUsage(response.token_usage);
            }
          } catch (err) {
            console.error(`Error consolidating group ${index + 1}:`, err);
            const errorMessage = err instanceof Error ? err.message : String(err);
            this.toastr.error(`Failed to consolidate group "${group.group_rationale.substring(0, 40)}...": ${errorMessage}`, 'Group Error');
          }
        }
      }

      // Step 4: Process ungrouped tests and finalize the suite
      const allGroupedTestIds = new Set(this.statoSessioneService.getConsolidationGroups().flatMap(g => g.test_ids));
      const ungroupedTests = rawTests.filter(t => !allGroupedTestIds.has(t.metadata.displayId));
      const finalUngroupedTests: DynamicRefinedTestCase[] = ungroupedTests.map(t => ({
        id: `UNCONSOLIDATED-${t.metadata.displayId}`,
        type: 'UNGROUPED_RAW',
        title: t.data?.TITLE || 'Original Ungrouped Test',
        rationale: 'This test was unique and not consolidated.',
        data: t.data,
        sourceTestIds: [t.metadata.displayId],
        sourceMicroScenarioIds: t.metadata.microScenarioId ? [t.metadata.microScenarioId] : []
      }));
      this.statoSessioneService.addGlobalConsolidatedTests(finalUngroupedTests);

      const allUnsequencedConsolidated = this.statoSessioneService.getGlobalConsolidatedTests();
      if (allUnsequencedConsolidated.length > 0) {
        const displayableTests = this.transformToDisplayable(allUnsequencedConsolidated);
        const sortedDisplayableTests = this.testIdService.sortTestsByType(displayableTests);
        const sequentialIdMap = new Map<string, number>();
        sortedDisplayableTests.forEach((test, index) => {
          sequentialIdMap.set(test.metadata.displayId, index + 1);
        });
        const finalProcessedTests = allUnsequencedConsolidated.map(test => ({
          ...test,
          sequentialId: sequentialIdMap.get(test.id)
        }));
        this.statoSessioneService.updateGlobalConsolidatedTests(finalProcessedTests);
      }

      if (noGroupsFound) {
        this.toastr.success('No redundant groups found. The test suite is already optimized.');
      } else {
        this.toastr.success('Global consolidation completed successfully!', 'Success');
      }
      this.displaySourceSubject.next('global-consolidated');

      // Step 5: Save the final state
      try {
        await this.sessionPersistService.saveCurrentSession('tests').pipe(take(1)).toPromise();
        this.toastr.success(`Progress saved`);
      } catch (saveError) {
        console.error('Error saving tests checkpoint:', saveError);
        const errorMessage = saveError instanceof Error ? saveError.message : String(saveError);
        this.toastr.error(`Failed to save tests checkpoint: ${errorMessage}`, '', { timeOut: 3000, positionClass: 'toast-bottom-right' });
      }
    } catch (err) {
      console.error('A fatal error occurred during the consolidation process:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      this.toastr.error(`An error occurred during consolidation: ${errorMessage}`, 'Error');
    } finally {
      this._resetConsolidationState();
    }
  }

  private _resetConsolidationState(): void {
    this.isGrouping = false;
    this.isConsolidating = false;
    this.consolidationProgress = { current: 0, total: 0 };
    this.cd.detectChanges();
  }

  private transformToRaw(tests: TestWithMetadata[]): RawTestCase[] {
    return tests.map(test => ({
      metadata: { displayId: test.metadata.displayId },
      data: test.data
    }));
  }

  toggleDisplaySource(): void {
    const currentSource = this.displaySourceSubject.getValue();
    const newSource = currentSource === 'global-consolidated' ? 'generated' : 'global-consolidated';
    this.displaySourceSubject.next(newSource);
  }

  public handleTestUpdate(updatedTest: TestWithMetadata): void {
    const source = this.displaySourceSubject.getValue();
    if (source === 'global-consolidated') {
      this.statoSessioneService.updateGlobalConsolidatedTest(updatedTest);
      this.toastr.success(`Final Test #${updatedTest.metadata.sequentialId} updated.`);
    } else {
      this.statoSessioneService.updateTest(updatedTest);
      this.toastr.success(`Raw Test #${updatedTest.metadata.sequentialId} updated.`);
    }
  }

  public handleTestDelete(testToDelete: TestWithMetadata): void {
    const source = this.displaySourceSubject.getValue();
    if (source === 'global-consolidated') {
      this.statoSessioneService.deleteGlobalConsolidatedTest(testToDelete.metadata.displayId);
      this.toastr.warning(`Final Test #${testToDelete.metadata.sequentialId} deleted.`);
    } else {
      this.statoSessioneService.deleteTest(testToDelete.metadata.displayId);
      this.toastr.warning(`Raw Test #${testToDelete.metadata.sequentialId} deleted.`);
    }
  }

  private parseJsonToConsolidatedTests(
    testObjects: any[],
    groupRationale: string,
    groupIndex: number,
    sourceTests: TestWithMetadata[]
  ): DynamicRefinedTestCase[] {
    const sourceTestIds = sourceTests.map(t => t.metadata.displayId);
    const sourceMicroScenarioIds = [...new Set(sourceTests.map(t => t.metadata.microScenarioId).filter(Boolean))];
    const parsedTests: DynamicRefinedTestCase[] = testObjects.map((testObj, testIndex) => {
      const newTest: DynamicRefinedTestCase = {
        id: `CONSOLIDATED-${groupIndex}-${testIndex}-${Date.now()}`,
        type: 'CONSOLIDATED',
        title: testObj.TITLE || testObj.title || `Consolidated Test ${testIndex + 1}`,
        rationale: groupRationale,
        data: testObj,
        sourceTestIds: sourceTestIds,
        sourceMicroScenarioIds: sourceMicroScenarioIds
      };
      return newTest;
    });
    return parsedTests;
  }

  private getTestType(test: TestWithMetadata): string {
    const typeValue = test.data?.TYPE;
    if (!typeValue || typeof typeValue !== 'string') {
      return 'OTHER';
    }
    return typeValue.toUpperCase().trim();
  }

  applyTestTypeFilter(type: string | null): void {
    this.currentTestTypeFilter = type;
    this.testTypeFilterSubject.next(type);
  }

  applyRawTestTypeFilter(type: string | null): void {
    this.currentRawTestTypeFilter = type;
    this.rawTestTypeFilterSubject.next(type);
  }

  private isTestForReview(test: TestWithMetadata): boolean {
    const title = test.data?.TITLE;
    if (typeof title === 'string') {
      return title.trim().toUpperCase().startsWith('[REVIEW]');
    }
    return false;
  }

  applyReviewFilter(filterState: 'all' | 'review' | 'non-review'): void {
    this.currentReviewFilter = filterState;
    this.reviewFilterSubject.next(filterState);
  }
}
